/**
 * stock-ship-estimate.js
 * ---------------------------------------------------------------------------
 * Flips the "Usually Ships" estimate on the product page and in the cart when a
 * customer asks for more of an item than is actually on hand, so expectations
 * are set before they buy. Scaffolds off the existing shipping-estimate block
 * and the hidden "Usually Ships" line-item property.
 *
 * Scope:
 *   Product page — non-customizable products only. Customizable products
 *     (those that render [data-property-modifiers]) route their PDP ship-time
 *     display through property-modifiers.js and are skipped on the PDP here.
 *   Cart — all products, handled per line. A customizable line ordered plain
 *     (no customization selected) oversells like a normal stocked item; a
 *     customized, made-to-order line keeps its own lead time and is left alone.
 *
 * Client-approved behavior (Paulette + Robert, 2026-06-01):
 *   Product page, requested + already-in-cart > on hand →
 *     "Only {n} in stock. Additional units ship in {range}." and the submitted
 *     properties[Usually Ships] becomes the clean "{range}" (no count).
 *   Cart → clean "Usually Ships: {range}" (no count); recomputed live on
 *     quantity change. Reverts to "Next Business Day" within stock.
 *
 * Only fires for inventory-tracked, "continue" (backorder) policy variants that
 * have out_of_stock_ship_min/max set. "deny" variants are capped by Shopify.
 */
(function () {
  'use strict';

  var DEBUG = false; // flip to true to re-enable [StockShip] QA logging.
  function log() {
    if (DEBUG && window.console) {
      console.log.apply(console, ['[StockShip]'].concat([].slice.call(arguments)));
    }
  }

  // Mirrors _formatShipDays in property-modifiers.js. Keep the two in sync.
  function formatShipDays(min, max) {
    min = parseInt(min, 10) || 0;
    max = parseInt(max, 10) || min;
    if (min <= 0) return '';
    if (min >= 14) {
      var minW = Math.ceil(min / 7);
      var maxW = Math.ceil(max / 7);
      return minW === maxW
        ? minW + ' week' + (minW !== 1 ? 's' : '')
        : minW + '–' + maxW + ' weeks';
    }
    return min === max
      ? min + ' day' + (min !== 1 ? 's' : '')
      : min + '–' + max + ' days';
  }

  function oversoldSentence(onHand, rangeLabel) {
    return 'Only ' + onHand + ' in stock. Additional units ship in ' + rangeLabel + '.';
  }

  window.StockShipEstimate = {
    formatShipDays: formatShipDays,
    oversoldSentence: oversoldSentence,
  };

  function sectionOf(el) {
    return el.closest('product-info') || el.closest('.shopify-section') || document;
  }

  function camelToDataAttr(key) {
    return 'data-' + key.replace(/[A-Z]/g, function (m) { return '-' + m.toLowerCase(); });
  }

  var STOCK_ATTRS = [
    'isAvailable', 'isBackorder', 'inventoryQty', 'inventoryTracked',
    'inventoryPolicy', 'restockMinDays', 'restockMaxDays', 'baseMinDays', 'baseMaxDays',
  ];

  // ===========================================================================
  // PRODUCT PAGE
  // ===========================================================================
  function PdpStockEstimate(block) {
    this.block = block;
    this.section = sectionOf(block);
    this.labelEl = block.querySelector('[data-ship-estimate-label]');
    this.rangeEl = block.querySelector('[data-ship-estimate-range]');
    if (!this.rangeEl) return;

    // Server-rendered "normal" state, kept so we can revert cleanly (preserves
    // localized strings like "Next Business Day").
    this.normalRangeHTML = this.rangeEl.innerHTML;

    this.hiddenLabel = this.section.querySelector('input[name="properties[Usually Ships]"]');
    this.normalHiddenValue = this.hiddenLabel ? this.hiddenLabel.value : '';

    // Live count of this variant already in the cart. null = use the
    // server-rendered data-cart-quantity until the cart tells us otherwise.
    this.liveInCart = null;

    this.bind();
    this.evaluate();
  }

  PdpStockEstimate.prototype.qtyInput = function () {
    return this.section.querySelector(
      'quantity-input input[name="quantity"], input.quantity__input[name="quantity"]'
    );
  };

  PdpStockEstimate.prototype.currentVariantId = function () {
    var idInput = this.section.querySelector('[name="id"]');
    if (idInput && idInput.value) return String(idInput.value);
    var q = this.qtyInput();
    if (q && q.getAttribute('data-quantity-variant-id')) {
      return String(q.getAttribute('data-quantity-variant-id'));
    }
    return null;
  };

  PdpStockEstimate.prototype.requestedTotal = function () {
    var input = this.qtyInput();
    var requested = input ? (parseInt(input.value, 10) || 0) : 1;
    var inCart = this.liveInCart != null
      ? this.liveInCart
      : (input ? (parseInt(input.getAttribute('data-cart-quantity'), 10) || 0) : 0);
    return requested + inCart;
  };

  PdpStockEstimate.prototype.readStock = function () {
    var d = this.block.dataset;
    return {
      inStock: d.isAvailable !== 'false' && d.isBackorder !== 'true',
      tracked: d.inventoryTracked === 'true',
      policy: d.inventoryPolicy,
      onHand: parseInt(d.inventoryQty, 10),
      restockMin: parseInt(d.restockMinDays, 10) || 0,
      restockMax: parseInt(d.restockMaxDays, 10) || 0,
    };
  };

  PdpStockEstimate.prototype.bind = function () {
    var self = this;

    this.section.addEventListener('change', function (e) {
      if (e.target && e.target.matches && e.target.matches('input[name="quantity"]')) {
        self.evaluate();
      }
    });

    if (window.FoxTheme && FoxTheme.pubsub && FoxTheme.pubsub.PUB_SUB_EVENTS) {
      // Variant change: the shipping block isn't re-rendered, so pull fresh
      // stock attributes from the section in the payload.
      FoxTheme.pubsub.subscribe(FoxTheme.pubsub.PUB_SUB_EVENTS.variantChange, function (payload) {
        self.onVariantChange(payload);
      });
      // Cart change: keep the live in-cart count for this variant current, so
      // "requested + in cart" is accurate without a page reload.
      FoxTheme.pubsub.subscribe(FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate, function (payload) {
        self.onCartUpdate(payload);
      });
    }
  };

  PdpStockEstimate.prototype.onCartUpdate = function (payload) {
    var cart = payload && payload.cart;
    if (!cart || !cart.items) return;
    var vid = this.currentVariantId();
    if (!vid) return;
    var total = 0;
    cart.items.forEach(function (it) {
      if (String(it.variant_id || it.id) === vid) total += it.quantity;
    });
    if (this.liveInCart === total) return; // this variant's cart qty didn't change
    this.liveInCart = total;
    log('PDP cart update: variant', vid, 'now in cart =', total);
    this.evaluate();
  };

  PdpStockEstimate.prototype.onVariantChange = function (payload) {
    try {
      var html = payload && payload.data && payload.data.html;
      var fresh = html && html.querySelector('[data-shipping-estimate]');
      if (fresh) {
        STOCK_ATTRS.forEach(function (key) {
          var attr = camelToDataAttr(key);
          if (fresh.hasAttribute(attr)) this.block.setAttribute(attr, fresh.getAttribute(attr));
        }, this);
        var freshRange = fresh.querySelector('[data-ship-estimate-range]');
        if (freshRange) this.normalRangeHTML = freshRange.innerHTML;
      }
    } catch (e) { /* fall through */ }
    if (this.hiddenLabel) this.normalHiddenValue = this.hiddenLabel.value;
    // New variant: fall back to the refreshed data-cart-quantity until the next
    // cart update tells us this variant's live count.
    this.liveInCart = null;
    this.evaluate();
  };

  PdpStockEstimate.prototype.oversold = function () {
    var s = this.readStock();
    if (!s.inStock || !s.tracked || s.policy !== 'continue') return null;
    if (s.restockMin <= 0 || isNaN(s.onHand)) return null;
    // Oversold means POSITIVE stock on hand ordered beyond it ("some now, the
    // rest later"). A variant with nothing on hand is a plain backorder and
    // already renders the standard "Usually Ships: {range}" line server-side, so
    // never treat it as oversold or surface a zero/negative count here.
    if (s.onHand <= 0) return null;
    if (this.requestedTotal() <= s.onHand) return null;
    return s;
  };

  PdpStockEstimate.prototype.evaluate = function () {
    var s = this.oversold();
    if (s) {
      var range = formatShipDays(s.restockMin, s.restockMax);
      if (this.labelEl) this.labelEl.hidden = true;
      this.rangeEl.textContent = oversoldSentence(s.onHand, range);
      if (this.hiddenLabel) this.hiddenLabel.value = range;
      log('PDP oversold: requested+inCart', this.requestedTotal(), '> onHand', s.onHand, '→', range);
    } else {
      if (this.labelEl) this.labelEl.hidden = false;
      this.rangeEl.innerHTML = this.normalRangeHTML;
      if (this.hiddenLabel) this.hiddenLabel.value = this.normalHiddenValue;
    }
  };

  function init(root) {
    (root || document).querySelectorAll('[data-shipping-estimate]').forEach(function (block) {
      if (sectionOf(block).querySelector('[data-property-modifiers]')) return; // customizable → property-modifiers.js
      if (block._pdpStockEstimate) return;
      block._pdpStockEstimate = new PdpStockEstimate(block);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { init(document); });
  } else {
    init(document);
  }
  document.addEventListener('shopify:section:load', function (e) { init(e.target); });

  // ===========================================================================
  // CART — live recompute of "Usually Ships" when a line quantity exceeds stock
  // ===========================================================================
  var USUALLY_SHIPS = 'Usually Ships';
  var cartReconcileInFlight = false;
  var lastSignature = '';

  // Fingerprint of just the managed lines (variant, qty, stored value). Cart
  // events that don't change this — e.g. the insurance app updating its own
  // line — are ignored so we don't re-check on every unrelated cart mutation.
  function signatureOf(cart) {
    if (!cart || !cart.items) return '';
    var parts = [];
    cart.items.forEach(function (item) {
      if (!item.properties || !(USUALLY_SHIPS in item.properties)) return;
      parts.push(
        String(item.variant_id || item.id) + ':' + item.quantity + ':' +
        String(item.properties[USUALLY_SHIPS] || '')
      );
    });
    return parts.sort().join('|');
  }

  function cartChangeUrl() {
    return (window.FoxTheme && FoxTheme.routes && FoxTheme.routes.cart_change_url) || '/cart/change.js';
  }
  function cartUrl() {
    return (window.FoxTheme && FoxTheme.routes && FoxTheme.routes.cart_url) || '/cart';
  }

  function fetchStock(handle, cache) {
    if (cache[handle]) return cache[handle];
    var p = fetch('/products/' + handle + '?section_id=stock-inventory', { headers: { Accept: 'text/html' } })
      .then(function (r) { return r.text(); })
      .then(function (html) {
        var doc = new DOMParser().parseFromString(html, 'text/html');
        var el = doc.querySelector('[data-stock-inventory]');
        if (!el) { log('cart: stock section had no data for', handle); return null; }
        try { return JSON.parse(el.textContent); }
        catch (e) { log('cart: bad stock JSON for', handle, e); return null; }
      })
      .catch(function (e) { log('cart: stock fetch failed for', handle, e); return null; });
    cache[handle] = p;
    return p;
  }

  // Desired value is based on the variant's TOTAL quantity across every line,
  // not a single line — otherwise two lines of the same product (e.g. 5 + 18)
  // each read as "in stock" while together they exceed it.
  function desiredLabel(stock, variantId, totalQty, lineProps) {
    if (!stock) return null;
    // Customizable products are managed per line: a line with any customization
    // selected is made-to-order with its own lead time and is left untouched;
    // a line ordered plain is treated like a normal stocked item below.
    if (stock.has_modifiers && lineIsCustomized(lineProps, stock.modifier_property_names)) return null;
    var v = stock.variants && stock.variants[String(variantId)];
    if (!v || !v.manage) return null;
    var onHand = parseInt(v.qty, 10);
    if (isNaN(onHand) || !stock.oversold_label) return null;
    return totalQty > onHand ? stock.oversold_label : stock.normal_label;
  }

  // True if this cart line has any of the product's modifier properties set —
  // i.e. the customer actually customized it (made-to-order).
  function lineIsCustomized(props, names) {
    if (!props || !names || !names.length) return false;
    for (var i = 0; i < names.length; i++) {
      var val = props[names[i]];
      if (val != null && String(val).trim() !== '') return true;
    }
    return false;
  }

  // Update the visible "Usually Ships: …" text on every cart line of a variant,
  // in place — no full cart re-render (which would re-fetch recommendations and
  // cause the "Pairs well with" flicker).
  function updateLineDom(variantId, desired, modifierNames) {
    var lines = document.querySelectorAll('.cart-item[data-variant-id="' + variantId + '"]');
    lines.forEach(function (li) {
      if (lineDomIsCustomized(li, modifierNames)) return; // made-to-order line — leave it
      li.querySelectorAll('dt').forEach(function (dt) {
        if (!/Usually Ships/i.test(dt.textContent)) return;
        var dd = (dt.parentElement && dt.parentElement.querySelector('dd')) || dt.nextElementSibling;
        if (dd) dd.textContent = desired;
      });
    });
  }

  // DOM-side mirror of lineIsCustomized: properties render as
  // <dt><strong>Name:</strong></dt>, so a row is customized when any dt label
  // matches a modifier property name. Guards the rare case of a customized and a
  // plain line sharing one variant — we must not overwrite the customized one.
  function lineDomIsCustomized(li, modifierNames) {
    if (!modifierNames || !modifierNames.length) return false;
    var dts = li.querySelectorAll('dt');
    for (var i = 0; i < dts.length; i++) {
      var label = dts[i].textContent.replace(/[:\s]+$/, '').trim().toLowerCase();
      for (var j = 0; j < modifierNames.length; j++) {
        if (label === String(modifierNames[j]).trim().toLowerCase()) return true;
      }
    }
    return false;
  }

  function reconcileCart(cart) {
    if (cartReconcileInFlight || !cart || !cart.items || !cart.items.length) return;

    var candidates = [];
    cart.items.forEach(function (item) {
      if (!item.properties || !(USUALLY_SHIPS in item.properties)) return;
      candidates.push({
        key: item.key,
        handle: item.handle,
        variantId: String(item.variant_id || item.id),
        qty: item.quantity,
        current: String(item.properties[USUALLY_SHIPS] || ''),
        properties: item.properties,
      });
    });
    if (!candidates.length) return;
    lastSignature = signatureOf(cart);

    var cache = {};
    Promise.all(candidates.map(function (c) {
      return Promise.resolve(fetchStock(c.handle, cache)).then(function (stock) {
        c.stock = stock;
        c.modifierNames = (stock && stock.modifier_property_names) || [];
        // Customized lines are made-to-order — built regardless of stock — so
        // they neither oversell themselves nor draw from the on-hand pool.
        c.customized = !!(stock && stock.has_modifiers && lineIsCustomized(c.properties, c.modifierNames));
        return c;
      });
    })).then(function (lines) {
      // On-hand demand per variant = quantity of PLAIN lines only. Made-to-order
      // lines don't consume stock, so they're left out of the oversell total
      // (otherwise a customized sibling would push a plain line over the edge).
      var plainTotals = {};
      lines.forEach(function (c) {
        if (c.customized) return;
        plainTotals[c.variantId] = (plainTotals[c.variantId] || 0) + c.qty;
      });

      lines.forEach(function (c) {
        c.desired = desiredLabel(c.stock, c.variantId, plainTotals[c.variantId] || 0, c.properties);
      });

      // 1) Update the on-screen text immediately for every managed variant.
      var seen = {};
      lines.forEach(function (c) {
        if (c.desired !== null && !seen[c.variantId]) {
          seen[c.variantId] = true;
          updateLineDom(c.variantId, c.desired, c.modifierNames);
        }
      });
      // 2) Persist any stored values that disagree, so the order record matches.
      var fixes = lines.filter(function (c) {
        return c.desired !== null && c.desired.trim() !== c.current.trim();
      });
      log('cart reconcile:', lines.map(function (c) {
        return { qty: c.qty, plainTotal: plainTotals[c.variantId] || 0, current: c.current, desired: c.desired };
      }));
      // One silent write per pass; siblings (if any) settle on the next cart
      // event. No re-publish — the order stays correct without a visible reload.
      if (fixes.length) persistFix(fixes[0]);
    });
  }

  function persistFix(c) {
    cartReconcileInFlight = true;
    var props = {};
    Object.keys(c.properties).forEach(function (k) { props[k] = c.properties[k]; });
    props[USUALLY_SHIPS] = c.desired;
    log('cart persist → change.js', { key: c.key, qty: c.qty, to: c.desired });

    fetch(cartChangeUrl(), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ id: c.key, quantity: c.qty, properties: props }),
    })
      .then(function (r) { return r.json(); })
      .then(function (state) {
        cartReconcileInFlight = false;
        log('cart persist ← ok, item_count', state && state.item_count);
        // Re-read once to catch any remaining stored mismatch (no re-render).
        scheduleReconcile();
      })
      .catch(function (e) { cartReconcileInFlight = false; log('cart persist failed', e); });
  }

  // Debounced so the insurance app's frequent cart events don't spam the
  // reconcile. Always re-reads /cart.js so we act on the freshest state.
  var reconcileTimer = null;
  function scheduleReconcile() {
    clearTimeout(reconcileTimer);
    reconcileTimer = setTimeout(function () {
      if (!document.querySelector('cart-items, [id^="CartDrawer-"], .cart')) return;
      fetch(cartUrl() + '.js', { headers: { Accept: 'application/json' } })
        .then(function (r) { return r.json(); })
        .then(function (cart) { reconcileCart(cart); })
        .catch(function () {});
    }, 300);
  }

  document.addEventListener('cart:updated', function (e) {
    // Skip when the managed lines are unchanged (e.g. insurance-only updates).
    var cart = e.detail && e.detail.cart;
    if (cart && signatureOf(cart) === lastSignature) return;
    scheduleReconcile();
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', scheduleReconcile);
  } else {
    scheduleReconcile();
  }
})();
